import { Alert, Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import type { TachoActivityDayDto, TachoActivityResponse } from '@oip/contracts';
import { DetailCard } from '../../components/common/DetailCard';
import { cardIcon } from '../../components/common/cardIcons';
import {
  ACTIVITY_STATES,
  clockAt,
  dayTotals,
  isSilent,
  duration,
  stateColor,
  stateLabel,
} from './tachoActivity';

/**
 * A tachograph's work state, day by day.
 *
 * The chart every tachograph analysis tool draws: one row per day, a 24-hour
 * axis, and the driver's activity as bands across it. The drivers'-hours table
 * is a snapshot of right now; this is the shape of a shift, which is what
 * somebody reads when they want to know what actually happened.
 *
 * ⚠️ **It DRAWS; it does not judge.** An analysis report names an infringement
 * and cites an Article of Reg (EU) 165/2014. This cannot, because the ruleset
 * the vehicle runs under is something OIP is not told
 * (`tachograph-parameters.md` §8 Q5) — so every band here is a recorded state
 * and nothing on the card is a verdict. The caption says so rather than leaving
 * an absent warning to be read as compliance.
 *
 * ⚠️ **The TRACK is the unobserved ground**, exactly as on the state timeline.
 * Bands are drawn on top of it, so anywhere the track shows through is time the
 * tachograph said nothing about — which must never look like rest.
 */
export function TachoActivityCard({
  data,
  subtitle,
}: {
  data: TachoActivityResponse;
  subtitle?: string;
}): JSX.Element {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';

  /*
   * ⚠️ Slot 2 is shown only when it reported. Most vehicles are single-crewed,
   * so a permanent empty second timeline per day would double the height of the
   * card to say nothing — and an empty row beside a full one reads as a fault
   * rather than as an ordinary vehicle with one driver.
   */
  const slots = data.slots.filter((s) => s.reported > 0);
  const nothing = slots.length === 0;

  return (
    <DetailCard
      icon={cardIcon('timeline')}
      title="Activity by day"
      subtitle={subtitle ?? `${data.vehicleName} · ${data.days.length} days`}
    >
      <Stack spacing={2} sx={{ p: 2 }}>
        {data.truncated && (
          /*
           * ⚠️ Said out loud. Tacho parameters have no hourly rollup by design,
           * so raw samples are the only source and raw is dropped at the
           * retention boundary. Quietly serving thirty days under a request for
           * sixty would read as a fleet that had been idle for a month.
           */
          <Alert severity="info">
            Showing {data.days.length} days. Tachograph readings are kept for{' '}
            {data.retentionDays} days and are not summarised by the hour, so a longer range
            cannot be drawn.
          </Alert>
        )}

        {nothing ? (
          /*
           * ⚠️ Distinguishes the two absences, as every other surface here does.
           * A vehicle with no tachograph is normal and needs no chasing; one
           * whose tachograph stopped is a finding.
           */
          <Typography variant="body2" color="text.secondary">
            No work state was recorded for this vehicle in the last {data.days.length} days.
            Either no tachograph is fitted, or it has stopped reporting — the drivers’ hours
            table above says which vehicles are still sending.
          </Typography>
        ) : (
          slots.map((slot) => (
            <Box key={slot.slot}>
              {slots.length > 1 && (
                <Typography variant="overline" color="text.secondary" display="block">
                  Driver {slot.slot}
                </Typography>
              )}
              <HourAxis />
              <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                {slot.days.map((day) => (
                  <DayRow key={day.date} day={day} dark={dark} />
                ))}
              </Stack>
            </Box>
          ))
        )}

        <Legend dark={dark} />

        <Typography variant="caption" color="text.secondary">
          Each band is a state the tachograph recorded. Nothing here is an infringement and
          nothing is thresholded: whether any of it breaches anything depends on the ruleset
          the vehicle runs under, which OIP is not told. Where the track shows through, the
          tachograph reported nothing — that is unobserved time, not rest. All times are UTC.
        </Typography>
      </Stack>
    </DetailCard>
  );
}

/**
 * Hours across the top, once per slot rather than once per row.
 *
 * ⚠️ **Its padding MUST equal the row's label and totals columns at every
 * breakpoint, or the axis lies.** It was `pl: 0` on xs while the rows kept a
 * 72px date column, so at 430px the axis sat 80px left of the track it labels —
 * an 18:00 band read as roughly 13:00. Nothing about the DOM was wrong and no
 * assertion would have caught it; found by screenshotting the phone viewport.
 * The numbers are `label width + gap` and `totals width + gap`.
 */
function HourAxis(): JSX.Element {
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'flex-end', pl: { xs: '80px', sm: '96px' }, pr: '132px' }}
    >
      <Box sx={{ position: 'relative', flex: 1, height: 14 }}>
        {[0, 3, 6, 9, 12, 15, 18, 21, 24].map((h) => (
          <Typography
            key={h}
            variant="caption"
            color="text.secondary"
            sx={{
              /*
               * ⚠️ Every THREE hours from `sm` up, every SIX on a phone. At
               * 430px the full set collides — measured as "21" and "24" running
               * together into "2124", which is the fault already recorded
               * against the state timeline's axis. Thinned rather than
               * shrunk: smaller type would be unreadable rather than merely
               * crowded.
               */
              display: h % 6 === 0 ? 'block' : { xs: 'none', sm: 'block' },
              position: 'absolute',
              left: `${(h / 24) * 100}%`,
              // The last label would hang off the right edge; pull it back onto
              // the axis rather than letting it widen the card.
              transform: h === 24 ? 'translateX(-100%)' : h === 0 ? 'none' : 'translateX(-50%)',
              fontSize: 10,
              lineHeight: 1,
            }}
          >
            {h}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

function DayRow({ day, dark }: { day: TachoActivityDayDto; dark: boolean }): JSX.Element {
  const silent = isSilent(day);
  const totals = dayTotals(day);
  const label = new Date(`${day.date}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant="caption"
          sx={{ width: { xs: 72, sm: 88 }, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
          color={silent ? 'text.disabled' : 'text.primary'}
        >
          {label}
        </Typography>

        <Box
          sx={{
            position: 'relative',
            flex: 1,
            height: 20,
            borderRadius: 0.5,
            // The unobserved ground. Anywhere this shows through, nothing was
            // reported — and that must never read as rest.
            bgcolor: 'action.hover',
            overflow: 'hidden',
          }}
        >
          {day.bands.map((b, i) => (
            <Tooltip
              key={`${b.state}-${b.fromMinute}-${i}`}
              title={
                `${stateLabel(b.state)} · ${clockAt(b.fromMinute)}–${clockAt(b.toMinute)} UTC` +
                ` · ${duration(b.toMinute - b.fromMinute)}` +
                (b.continuesBefore ? ' · continues from the previous day' : '') +
                (b.continuesAfter ? ' · continues into the next day' : '') +
                (b.endedOnGap ? ' · telemetry stopped here — what followed is unknown' : '')
              }
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: `${(b.fromMinute / 1440) * 100}%`,
                  // A single reading has zero width and would vanish entirely.
                  // Given a floor, so an observation is never invisible.
                  width: `max(${((b.toMinute - b.fromMinute) / 1440) * 100}%, 2px)`,
                  top: 0,
                  bottom: 0,
                  bgcolor: stateColor(b.state, dark),
                  // A band the telemetry cut fades and carries a dotted edge, so
                  // a reader sees the evidence stopped rather than the state
                  // changing.
                  opacity: b.endedOnGap ? 0.55 : 1,
                  borderRight: b.endedOnGap ? '2px dotted' : 'none',
                  borderColor: 'text.secondary',
                }}
              />
            </Tooltip>
          ))}
        </Box>

        <Box sx={{ width: 124, flexShrink: 0, textAlign: 'right' }}>
          {silent ? (
            <Typography variant="caption" color="text.disabled">
              not reported
            </Typography>
          ) : (
            <Stack direction="row" spacing={0.75} justifyContent="flex-end" flexWrap="wrap">
              {totals.map((t) => (
                <Typography
                  key={t.state}
                  variant="caption"
                  sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                  color="text.secondary"
                >
                  <Box component="span" sx={{ color: stateColor(t.state, dark) }}>
                    ●
                  </Box>{' '}
                  {t.value}
                </Typography>
              ))}
            </Stack>
          )}
        </Box>
      </Box>

    </Box>
  );
}

function Legend({ dark }: { dark: boolean }): JSX.Element {
  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
      {ACTIVITY_STATES.map((s) => (
        <Stack key={s} direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{ width: 14, height: 10, borderRadius: 0.5, bgcolor: stateColor(s, dark) }}
          />
          {/* The word, always — a legend carried by colour alone says nothing
              to a reader who cannot distinguish two of them. */}
          <Typography variant="caption" color="text.secondary">
            {stateLabel(s)}
          </Typography>
        </Stack>
      ))}
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box sx={{ width: 14, height: 10, borderRadius: 0.5, bgcolor: 'action.hover' }} />
        <Typography variant="caption" color="text.secondary">
          Nothing reported
        </Typography>
      </Stack>
    </Stack>
  );
}
